import { User } from '../context/pendo-context';

interface UTMParameters {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
}

interface StoredUTMData extends UTMParameters {
    _captured_at: string;
    _domain: string;
    _url: string;
}

interface PendoVisitor extends UTMParameters {
    id: string;
    email?: string;
    full_name?: string;
    role?: string;
}

interface PendoAccount {
    id: string;
    name: string;
    accountType: string;
    subscriptionType: string;
    creationDate: string;
    hasFailedPayment?: boolean;
}

interface PendoMetaData {
    visitor: PendoVisitor;
    account?: PendoAccount;
}

type PendoExtraData = Partial<Pick<PendoAccount, 'hasFailedPayment'>>;

type PendoFunction = (metaData: PendoMetaData) => void;

const initializePendo = (user: User | null, pendoFunction: PendoFunction | null, extra?: PendoExtraData): void => {
    // Configuration - set to null to disable expiration
    const UTM_EXPIRATION_DAYS: number | null = null; // Change to 30, 90, etc. to enable expiration
    const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

    // Function to get UTM parameters from URL or local storage
    const getUTMParameters = (): UTMParameters => {
        const searchParams = new URLSearchParams(window.location.search);

        // Create domain-specific storage key to avoid conflicts with other sites
        const domain = window.location.hostname;
        const storageKey = `utm_parameters_${domain}`;

        // Check if UTM parameters exist in current URL
        const currentUtmParams: UTMParameters = {
            utm_source: searchParams.get('utm_source') || undefined,
            utm_medium: searchParams.get('utm_medium') || undefined,
            utm_campaign: searchParams.get('utm_campaign') || undefined,
            utm_term: searchParams.get('utm_term') || undefined,
            utm_content: searchParams.get('utm_content') || undefined
        };

        // If we have UTM parameters in URL, store them in local storage with timestamp
        const hasCurrentUtm = Object.values(currentUtmParams).some(value => value !== undefined);
        if (hasCurrentUtm) {
            // Check if we already have stored UTM parameters (first-touch attribution)
            const existingUtm = localStorage.getItem(storageKey);
            if (existingUtm) {
                // Already have UTM data, don't overwrite (first-touch wins)
                try {
                    const parsed: StoredUTMData = JSON.parse(existingUtm);
                    const { _captured_at, _domain, _url, ...utmOnly } = parsed;
                    return utmOnly;
                } catch {
                    // If parsing fails, continue with new UTM data
                }
            }

            const filteredCurrent = Object.fromEntries(
                Object.entries(currentUtmParams).filter(([_, value]) => value !== undefined)
            ) as UTMParameters;

            // Add timestamp and domain info
            const utmData: StoredUTMData = {
                ...filteredCurrent,
                _captured_at: new Date().toISOString(),
                _domain: domain,
                _url: window.location.href
            };

            localStorage.setItem(storageKey, JSON.stringify(utmData));
            return filteredCurrent; // Return only UTM params, not metadata
        }

        // Otherwise, try to get from local storage
        try {
            const storedUtm = localStorage.getItem(storageKey);
            if (storedUtm) {
                const parsed: StoredUTMData = JSON.parse(storedUtm);

                // Check expiration only if UTM_EXPIRATION_DAYS is set
                if (UTM_EXPIRATION_DAYS !== null) {
                    const capturedAt = new Date(parsed._captured_at);
                    const expirationDate = new Date(capturedAt.getTime() + UTM_EXPIRATION_DAYS * MILLISECONDS_PER_DAY);

                    if (Date.now() > expirationDate.getTime()) {
                        // Data is too old, remove it
                        localStorage.removeItem(storageKey);
                        return {};
                    }
                }

                // Remove metadata fields before returning
                const { _captured_at, _domain, _url, ...utmOnly } = parsed;
                return utmOnly;
            }
            return {};
        } catch {
            return {};
        }
    };

    // Get UTM parameters (from URL or storage)
    const utmParams = getUTMParameters();

    let pendoMetaData: PendoMetaData = {
        visitor: {
            id: 'VISITOR-UNIQUE-ID',
            ...utmParams
        }
    }

    if (user) {
        const fullName = `${user.firstName} ${user.lastName}`;
        pendoMetaData = {
            visitor: {
                id: user.loggedInUsername,
                email: user.email,
                full_name: fullName,
                role: user.userType,
                ...utmParams
            },
            account: {
                id: user.id,
                name: user.companyName,
                accountType: user.type,
                subscriptionType: user.subscriptionType,
                creationDate: user.createdOn,
                ...(extra?.hasFailedPayment != null && { hasFailedPayment: extra.hasFailedPayment }),
            }
        }
    }

    // Call Pendo function
    if (pendoFunction && typeof pendoFunction === 'function') {
        pendoFunction(pendoMetaData);
    }
}

export default initializePendo;
